import { useCallback, useState } from "react";
import {
  clearWelcome,
  welcomePending,
} from "../../auth/services/signupArrival";

/**
 * Whether to tell this reader the app is built for a desktop, and how to close
 * that for good.
 *
 * Two conditions, both of which have to hold. They are on a phone — the note is
 * about the screen being held, and there is nothing to tell somebody already on
 * a wide one. And this is a practice that signed up and has just come through
 * the end of its way in, the free trial or the paid checkout, reaching the app
 * for the first time. Said once, there, and then never again.
 *
 * Both halves of that second condition matter, and `welcomePending` is where
 * they are settled. Signing up alone is not enough — the dashboard is held
 * behind the trial offer, so the journey between the two is exactly where this
 * must not appear. Finishing a checkout alone is not enough either — an
 * established practice buying another term reaches the same screen.
 *
 * Deliberately not "has no brand yet", which is the question the brand dialog
 * behind it asks. Somebody who skipped brand setup in March and is signing in
 * on their phone in June would answer yes to that one, and they already know
 * what this app is — telling them where to run it a second time is nagging.
 *
 * It is a note, not a gate: nothing behind it is blocked, and the only thing it
 * asks for is a press to get rid of it.
 */

export interface DesktopNotice {
  /** Whether the note belongs on screen right now. */
  open: boolean;
  /** Closes it, and spends the arrival it was being kept for. */
  dismiss: () => void;
}

/**
 * @param onPhone Whether the screen is narrow enough for the note to be worth
 *   making. Passed in rather than asked here, so the page that already knows
 *   which of its two layouts it is drawing answers it once.
 */
export function useDesktopNotice(onPhone: boolean): DesktopNotice {
  // Dismissal within this page's life. Clearing the stored fact below covers a
  // reload and the rest of the visit; this is what closes the note on the
  // press, and what closes it where storage is unavailable.
  const [dismissed, setDismissed] = useState(false);

  const dismiss = useCallback(() => {
    setDismissed(true);
    clearWelcome();
  }, []);

  const open = onPhone && !dismissed && welcomePending();

  return { open, dismiss };
}

export default useDesktopNotice;
